import { Dispatch, SetStateAction, createContext } from 'react';
import { LicenseInfo } from '../../generated/graphql';
import { AuthState, CognitoUserInterface } from '../../types/auth-types';

export type UserRole = 'admin' | 'analyst' | 'viewer' | undefined;

export interface DetectionAutoCloseConfig {
  duration: string;
  updated_on: string;
  updated_by: string;
}

export type UserPrivileges = {
  [key: string]: boolean;
} & {
  detection_auto_close_config?: DetectionAutoCloseConfig;
};

export interface AmplifyUserContext {
  // Core state
  authState?: AuthState;
  user?: CognitoUserInterface;
  userAlias?: string;
  userRole?: UserRole;
  userLicense?: LicenseInfo | null | undefined;
  userLicenseLoading?: boolean;

  // Setters
  setAuthState: (state?: AuthState) => void;
  setUser: (user?: CognitoUserInterface) => void;
  setUserAlias: Dispatch<SetStateAction<string | undefined>>;
  setUserRole: Dispatch<SetStateAction<UserRole>>;

  // Privileges
  userPrivileges?: Record<string, UserPrivileges>;
  userPrivilegesLoading?: boolean;
  setUserPrivileges: (userPrivileges: Record<string, UserPrivileges>) => void;

  // Tenant info
  isOrgTenant?: boolean;
  isPOVTenant?: boolean;
  isPrimeTenant?: boolean;

  // lazy loading methods
  loadPrivilegesForTenant?: (tenantId: string) => Promise<UserPrivileges>;
  getCurrentTenantPrivileges?: () => Promise<UserPrivileges>;
  currentTenantPrivileges?: UserPrivileges;
  setUserPrivilegesLoading?: Dispatch<SetStateAction<boolean>>;
  setIsPrimeTenant: Dispatch<SetStateAction<boolean>>;
  isFederatedOrgTenant?: boolean;
}

export const AuthContext = createContext<AmplifyUserContext>({
  authState: undefined,
  setAuthState: () => null,
  user: undefined,
  setUser: () => null,
  userAlias: undefined,
  setUserAlias: () => null,
  userRole: undefined,
  setUserRole: () => null,
  userPrivileges: undefined,
  setUserPrivileges: () => null,
  userPrivilegesLoading: undefined,
  setUserPrivilegesLoading: () => null,
  userLicense: undefined,
  userLicenseLoading: undefined,
  isFederatedOrgTenant: undefined,
  isOrgTenant: undefined,
  isPOVTenant: undefined,
  isPrimeTenant: undefined,
  setIsPrimeTenant: () => null,
});
